import { Component, OnInit } from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../services/mdr-dashboards.service';
import { ApexChart, ApexDataLabels, ApexLegend, ApexNonAxisChartSeries, ApexPlotOptions, ApexResponsive, ApexStroke } from 'ng-apexcharts';
import { ThreatsWidgets } from '../../interfaces/mdr-dashboards-widgets.interface';
import { distinctUntilChanged } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { TimeframeData } from '../../../timeframe/interfaces/TimeframeData';
import { TimeframeService } from '../../../timeframe/services/timeframe.service';
import { OrganizationData } from '../../../organizations/interfaces/OrganizationData';
import { ThemeService } from '../../../../services/theme.service';

export type ChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    responsive: ApexResponsive[];
    dataLabels: ApexDataLabels;
    labels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    legend: ApexLegend;
    stroke: ApexStroke;
};

@Component({
    selector: 'app-mdr-dashboards-agent-actions',
    templateUrl: './mdr-dashboards-agent-actions.component.html',
    styleUrls: ['./mdr-dashboards-agent-actions.component.scss'],
    standalone: false,
})
export class MdrDashboardsAgentActionsComponent implements OnInit {
    public chartOptions: Partial<ChartOptions> | any;

    public dataSource: ThreatsWidgets;

    public constructor(
        public organizationService: OrganizationsService,
        public mdrDashboardsService: MdrDashboardsService,
        public timeframeService: TimeframeService,
        public themeService: ThemeService,
    ) {}

    public ngOnInit() {
        this.timeframeService
            .selectedTimeframe$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((timeframe: TimeframeData) => {
                this.updateDataSource(timeframe, this.organizationService.currentlySelectedOrganization());
            });
        this.organizationService
            .selectedOrganization$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((organization: OrganizationData) => {
                this.updateDataSource(this.timeframeService.currentlySelectedTimeframe(), organization);
            });
    }

    /**
     * Use this function to update each widget when one of the selectors (organization and timeframe) are changed
     * @param timeframe - retrieve data for the selected timeframe
     * @param organization - retrieve data for the selected organization
     */
    public updateDataSource(timeframe: TimeframeData | null, organization: OrganizationData | null) {
        this.dataSource = {
            indicatorOptions: [
                {
                    title: 'Alerts',
                    value: 468,
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/alerts', organization),
                    delta: '',
                    id: 'alerts_indicator',
                },
                {
                    title: 'Agent actions',
                    value: 329,
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/agent-actions', organization),
                    delta: '',
                    id: 'agent_actions_indicator',
                },
            ],
            alertTrend: {
                id: 'alerts-trend',
                noDataLegend: [],
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/alert-trend', organization),
                colors: ['#FFB74D'],
                strokeColors: ['#FFB74D'],
                title: 'Alert trend',
                columns: [],
                approximate: true,
            },
            mostMitigatedThreats: {
                id: 'most_mitigated_threats_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-mitigated-threats', organization),
                no_rows: 10,
                columns: ['category', 'name', 'value'],
                columnsWidth: [
                    {
                        column: 'category',
                        width: '25%',
                    },
                    {
                        column: 'name',
                        width: '65%',
                    },
                    {
                        column: 'value',
                        width: '10%',
                    },
                ],
                title: 'Most mitigated threats',
            },
            topAlerts: {
                id: 'top_alerts_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-alerts', organization),
                columns: ['name', 'value'],
                columnsWidth: [
                    {
                        column: 'name',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                no_rows: 10,
                title: 'Top alerts',
            },
            automaticActionsTrend: {
                id: 'automatic_actions_trend',
                noDataLegend: [
                    {
                        name: 'Block',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'Block and Disinfect',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'Disinfect Only',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'Delete',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'Quarantine',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                ],
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/automatic-actions-trend', organization),
                strokeColors: ['#FFB74D', '#EF5350', '#E90A0A', '#F02A42', '#922227'],
                colors: ['#FFE2B7', '#FBCA99', '#F9BAB9', '#EDACAF', '#D3A6A8'],
                title: 'Automatic actions trend',
                columns: [],
                approximate: true,
            },
        };
    }
}
